import type { ComponentType } from "react";
import type { RouteObject } from "react-router";

import { STEP_PATHS } from "@/auth/session";
import { SessionGate, StepGate } from "@/auth/session-gate";
import { ChangePasswordPage } from "@/pages/change-password";
import { ChooseAuthorityPage } from "@/pages/choose-authority";
import { EnterCodePage } from "@/pages/enter-code";
import { HomePage, NotFoundPage } from "@/pages/home";
import { SetUpCodesPage } from "@/pages/set-up-codes";
import { SignInPage } from "@/pages/sign-in";
import { IS_DESKTOP } from "@/desktop/mode";
import { DesktopStartPage } from "@/pages/desktop/start";
import { Navigate } from "react-router";

/** Pages loaded when first opened: [path, the page component]. */
function lazyPages(pages: [string, () => Promise<ComponentType>][]): RouteObject[] {
  return pages.map(([path, load]) => ({ path, lazy: async () => ({ Component: await load() }) }));
}

/** Every page, and the gate that decides who may see it. */
export const routes: RouteObject[] = [
  // The desktop checker has no sign-in: its start page confirms the licence (M14b).
  { path: "/sign-in", element: IS_DESKTOP ? <DesktopStartPage /> : <SignInPage /> },
  ...(IS_DESKTOP ? [{ path: "/licence", element: <DesktopStartPage /> }] : []),
  // Public: what an approved drawing's QR code opens (M12). No sign-in.
  {
    path: "/verify/:id",
    lazy: async () => ({ Component: (await import("@/pages/verify")).VerifyPage }),
  },
  {
    element: <StepGate path={STEP_PATHS.ENTER_MFA_CODE} />,
    children: [{ path: STEP_PATHS.ENTER_MFA_CODE, element: <EnterCodePage /> }],
  },
  {
    element: <StepGate path={STEP_PATHS.CHANGE_PASSWORD} />,
    children: [{ path: STEP_PATHS.CHANGE_PASSWORD, element: <ChangePasswordPage /> }],
  },
  {
    element: <StepGate path={STEP_PATHS.ENROL_MFA} />,
    children: [{ path: STEP_PATHS.ENROL_MFA, element: <SetUpCodesPage /> }],
  },
  {
    element: <StepGate path={STEP_PATHS.CHOOSE_AUTHORITY} />,
    children: [{ path: STEP_PATHS.CHOOSE_AUTHORITY, element: <ChooseAuthorityPage /> }],
  },
  {
    element: <SessionGate />,
    children: [
      {
        index: true,
        element: IS_DESKTOP ? <Navigate to="/pre-checks/new" replace /> : <HomePage />,
      },
      // Each role's pages load when first opened.
      {
        path: "pre-checks",
        lazy: async () => ({
          Component: (await import("@/pages/pre-checks/list")).PreCheckListPage,
        }),
      },
      {
        path: "pre-checks/new",
        lazy: async () => ({ Component: (await import("@/pages/pre-checks/new")).NewPreCheckPage }),
      },
      {
        path: "applications",
        lazy: async () => ({
          Component: (await import("@/pages/applications/list")).ApplicationListPage,
        }),
      },
      {
        path: "applications/new",
        lazy: async () => ({
          Component: (await import("@/pages/applications/new")).NewApplicationPage,
        }),
      },
      {
        path: "applications/:id",
        lazy: async () => ({
          Component: (await import("@/pages/applications/detail")).ApplicationDetailPage,
        }),
      },
      {
        path: "review",
        lazy: async () => ({ Component: (await import("@/pages/review/queue")).ReviewQueuePage }),
      },
      {
        path: "review/:id",
        lazy: async () => ({ Component: (await import("@/pages/review/detail")).ReviewPage }),
      },
      ...lazyPages([
        ["admin/members", () => import("@/pages/admin/members").then((m) => m.MembersPage)],
        ["admin/zones-uses", () => import("@/pages/admin/zones-uses").then((m) => m.ZonesUsesPage)],
        ["admin/rule-sets", () => import("@/pages/admin/rule-sets").then((m) => m.RuleSetsPage)],
        ["admin/rule-sets/:id", () => import("@/pages/admin/rule-set").then((m) => m.RuleSetPage)],
        [
          "admin/scrutiny-parameters",
          () => import("@/pages/admin/scrutiny-parameters").then((m) => m.ScrutinyParametersPage),
        ],
        ["admin/formulas", () => import("@/pages/admin/formulas").then((m) => m.FormulasPage)],
        ["admin/formulas/:item", () => import("@/pages/admin/formula").then((m) => m.FormulaPage)],
        ["admin/byelaws", () => import("@/pages/admin/byelaws").then((m) => m.ByelawsPage)],
        ["admin/byelaws/:id", () => import("@/pages/admin/byelaw").then((m) => m.ByelawPage)],
        [
          "admin/authority",
          () => import("@/pages/admin/authority").then((m) => m.AuthoritySettingsPage),
        ],
        ["admin/activity", () => import("@/pages/audit").then((m) => m.AdminAuditPage)],
        [
          "admin/integrations",
          () => import("@/pages/admin/integrations").then((m) => m.IntegrationsPage),
        ],
        ["admin/licences", () => import("@/pages/admin/licences").then((m) => m.LicencesPage)],
        // M45: the colour card beside AutoCAD (ADR-0063); M47, the AUBB card's (ADR-0066).
        ["guides/mmsgna", () => import("@/pages/guides/mmsgna").then((m) => m.MmsgnaGuidePage)],
        ["guides/aubb", () => import("@/pages/guides/aubb").then((m) => m.AubbGuidePage)],
        ["guides/obpas", () => import("@/pages/guides/obpas").then((m) => m.ObpasGuidePage)],
        ["golden", () => import("@/pages/golden/list").then((m) => m.GoldenSetPage)],
        ["golden/:id", () => import("@/pages/golden/detail").then((m) => m.GoldenDrawingPage)],
        [
          "platform/golden",
          () => import("@/pages/platform/golden").then((m) => m.PlatformGoldenPage),
        ],
        ["platform/states", () => import("@/pages/platform/states").then((m) => m.StatesPage)],
        [
          "platform/authorities",
          () => import("@/pages/platform/authorities").then((m) => m.AuthoritiesPage),
        ],
        [
          "platform/authorities/:id",
          () => import("@/pages/platform/authority").then((m) => m.AuthorityPage),
        ],
        [
          "platform/accounts",
          () => import("@/pages/platform/accounts").then((m) => m.AccountsPage),
        ],
        [
          "platform/accounts/:id",
          () => import("@/pages/platform/accounts").then((m) => m.AccountPage),
        ],
        ["platform/activity", () => import("@/pages/audit").then((m) => m.PlatformAuditPage)],
      ]),
      {
        path: "pre-checks/:id",
        lazy: async () => ({
          Component: (await import("@/pages/pre-checks/detail")).PreCheckDetailPage,
        }),
      },
      {
        // The conversion tool (M37).
        path: "pre-checks/:id/convert",
        lazy: async () => ({
          Component: (await import("@/pages/pre-checks/convert")).ConvertPage,
        }),
      },
      {
        // The QR code on a checked drawing's PDF (M24).
        path: "checks/:jobId",
        lazy: async () => ({
          Component: (await import("@/pages/checks/open")).OpenCheckPage,
        }),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
